import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FaSearch, FaCar, FaMotorcycle, FaTimes, FaChevronDown, FaChevronRight, FaEdit, FaTrash, FaPlus, FaCheckCircle, FaCamera, FaHeartbeat } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useVehicle } from "../contexts/VehicleContext";
import { useNavigate, Link } from "react-router-dom";
import {
  getVehicleImageUrl,
  getVehicleMakers,
  getModelsForMaker,
  getYearsForModel,
  getModificationsForYear,
  VEHICLE_CARD_IMAGE_PLACEHOLDER,
  stripDuplicateMakeFromModel
} from "../data/vehicleData";
import { categories } from "./SearchByCategory";
import { showToast } from "../utils/toast";

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/** Normalize stored month (1–12 or month name) to option value "1"…"12" (matches catalogue trim months) */
function vehicleMonthToOptionValue(m) {
  if (m == null || m === '') return '';
  const n = Number(m);
  if (Number.isFinite(n) && n >= 1 && n <= 12) return String(n);
  const s = String(m).trim();
  const idx = MONTH_NAMES.findIndex((x) => x.toLowerCase() === s.toLowerCase());
  if (idx >= 0) return String(idx + 1);
  return '';
}

const getVehicleCardTitle = (vehicle) => {
  if (vehicle?.nickname?.trim()) return vehicle.nickname.trim();
  const make = String(vehicle?.make || '').trim();
  const model = stripDuplicateMakeFromModel(vehicle?.make, vehicle?.model);
  return [make, model].filter(Boolean).join(' ') || 'Your vehicle';
};

export const SearchSection = ({ onClose, initialVehicle = null, embedded = false }) => {
  const { addVehicle, updateVehicle, removeVehicle, vehicles, selectVehicle, selectedVehicle,
    fetchMakes, fetchModels, fetchYears, fetchVariants, fetchMonths } = useVehicle();
  const navigate = useNavigate();

  // Modal states
  const [showVehicleTypeModal, setShowVehicleTypeModal] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [vehicleType, setVehicleType] = useState(""); // "car" or "twoWheeler"
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  // Form states
  const [selectedMaker, setSelectedMaker] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedModelId, setSelectedModelId] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedModification, setSelectedModification] = useState("");
  const [numberPlate, setNumberPlate] = useState("");
  const [vin, setVin] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [vehiclePhoto, setVehiclePhoto] = useState("");
  const photoInputRef = useRef(null);
  // Dropdown data — fetched from backend API (same source as VehicleCompatibilitySelector)
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [modifications, setModifications] = useState([]);

  const getModelOptionValue = useCallback((modelOption) => {
    if (!modelOption) return '';
    if (typeof modelOption === 'string') return modelOption;
    return String(
      modelOption.modelId ||
      modelOption.value ||
      modelOption.label ||
      ''
    ).trim();
  }, []);

  // Load makes from backend on mount, fallback to local data
  useEffect(() => {
    const loadMakes = async () => {
      try {
        if (fetchMakes) {
          const apiMakes = await fetchMakes();
          if (apiMakes && apiMakes.length > 0) { setMakes(apiMakes); return; }
        }
      } catch { }
      // Fallback to local data
      const type = vehicleType || 'car';
      const localMakers = getVehicleMakers(type);
      setMakes(localMakers.map(m => ({ value: m, label: m.replace(/_/g, ' ') })));
    };
    loadMakes();
  }, [fetchMakes, vehicleType]);

  // Get all vehicles sorted by creation date (most recent first)
  const sortedVehicles = useMemo(() => {
    return [...vehicles]
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.updatedAt || 0);
        const dateB = new Date(b.createdAt || b.updatedAt || 0);
        return dateB - dateA;
      });
  }, [vehicles]);

  /** Include current selection if it is no longer in catalogue (e.g. after trim removed) */
  const monthOptionsForSelect = useMemo(() => {
    const base = Array.isArray(months) ? [...months] : [];
    if (!selectedMonth) return base;
    const sel = String(selectedMonth);
    const exists = base.some(
      (m) => String(m.value) === sel || String(m.label) === sel
    );
    if (exists) return base;
    const n = Number(sel);
    if (Number.isFinite(n) && n >= 1 && n <= 12) {
      return [...base, { value: String(n), label: MONTH_NAMES[n - 1] }];
    }
    return [...base, { value: sel, label: sel }];
  }, [months, selectedMonth]);

  // Extract engine info from variant when present (e.g. 1.5L, 110cc)
  const getEngineInfo = (variant) => {
    const normalizedVariant = String(variant || '');
    const literMatch = normalizedVariant.match(/(\d+\.?\d*\s*L)\b/i);
    if (literMatch) return literMatch[1].replace(/\s+/g, '');
    const ccMatch = normalizedVariant.match(/(\d+\s*cc)\b/i);
    if (ccMatch) return ccMatch[1].replace(/\s+/g, '');
    return '';
  };

  // Get vehicle image
  const getVehicleImage = (make, model) => {
    const imageUrl = getVehicleImageUrl(make, model);
    return imageUrl || VEHICLE_CARD_IMAGE_PLACEHOLDER;
  };

  // HandleNew Spares
  const handleOemServiceKit = (vehicle) => {
    const params = new URLSearchParams({
      maker: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      mod: vehicle.variant || '',
    });
    navigate(`/oem-service-kit?${params.toString()}`);
  };

  // HandleUsed Spares
  const handleAftermarketServiceKit = (vehicle) => {
    const params = new URLSearchParams({
      maker: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      mod: vehicle.variant || '',
    });
    navigate(`/aftermarket-service-kit?${params.toString()}`);
  };

  // Handle View OEM Catalog
  const handleViewOemCatalog = (vehicle) => {
    const params = new URLSearchParams({
      maker: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      mod: vehicle.variant || '',
    });
    navigate(`/oem-catalog?${params.toString()}`);
  };

  const handleViewPassport = (vehicle) => {
    const id = vehicle?._id || vehicle?.id;
    navigate(id ? `/health-passport?vehicleId=${id}` : '/health-passport');
  };

  // Handle Category Click from Modal
  // Handle Edit Vehicle
  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowVehicleTypeModal(false);
    setTimeout(() => {
      setShowAddVehicleModal(true);
    }, 200);
  };

  // Handle Delete Vehicle
  const handleDelete = (vehicle) => {
    setVehicleToDelete(vehicle);
  };

  const confirmDelete = () => {
    if (vehicleToDelete) {
      removeVehicle(vehicleToDelete._id || vehicleToDelete.id);
      showToast.success(`${vehicleToDelete.make} ${vehicleToDelete.model} has been deleted`);
      setVehicleToDelete(null);
    }
  };

  const cancelDelete = () => {
    setVehicleToDelete(null);
  };


  // Pre-fill form when editing using backend API data
  useEffect(() => {
    const vehicleToEdit = initialVehicle || editingVehicle;
    if (vehicleToEdit) {
      setSelectedMaker(vehicleToEdit.make || "");
      currentMakerRef.current = vehicleToEdit.make || "";
      setSelectedModel(vehicleToEdit.model || "");
      setSelectedModelId("");
      setSelectedYear(vehicleToEdit.year || "");
      setSelectedMonth(vehicleMonthToOptionValue(vehicleToEdit.month));
      setSelectedModification(vehicleToEdit.variant || "");
      setNumberPlate(vehicleToEdit.registrationNumber || "");
      setVin(vehicleToEdit.vin || "");
      setFuelType(vehicleToEdit.fuelType || "");
      setVehiclePhoto(vehicleToEdit.photo || "");
      setVehicleType(vehicleToEdit.type || "car");

      // Load cascading dropdowns from backend
      if (vehicleToEdit.make && fetchModels) {
        fetchModels(vehicleToEdit.make).then((apiModels) => {
          setModels(apiModels);
          const matched = (apiModels || []).find(
            (m) => String(m?.label || '').trim().toLowerCase() === String(vehicleToEdit.model || '').trim().toLowerCase()
          );
          setSelectedModelId(getModelOptionValue(matched) || String(vehicleToEdit.model || ''));
        }).catch(() => { });
      }
      if (vehicleToEdit.make && vehicleToEdit.model) {
        const modelIdOpt = vehicleToEdit.modelId ? { modelId: vehicleToEdit.modelId } : {};
        Promise.all([
          fetchYears ? fetchYears(vehicleToEdit.make, vehicleToEdit.model, modelIdOpt) : Promise.resolve([]),
          vehicleToEdit.year && fetchMonths
            ? fetchMonths(vehicleToEdit.make, vehicleToEdit.model, vehicleToEdit.year, modelIdOpt)
            : Promise.resolve([]),
          fetchVariants
            ? fetchVariants(vehicleToEdit.make, vehicleToEdit.model, vehicleToEdit.year || '', modelIdOpt)
            : Promise.resolve([])
        ]).then(([yr, mo, vr]) => {
          setYears(yr);
          setMonths(mo || []);
          setModifications(vr.map((v) => v.value || v));
        }).catch(() => { });
      }
    } else {
      setSelectedMaker(""); setSelectedModel(""); setSelectedYear("");
      setSelectedModelId("");
      setSelectedMonth(""); setSelectedModification(""); setNumberPlate(""); setVin(""); setFuelType(""); setVehiclePhoto("");
      setModels([]); setYears([]); setModifications([]);
      setMonths([]);
      setVehicleType("");
    }
  }, [initialVehicle, editingVehicle, fetchModels, fetchYears, fetchMonths, fetchVariants, getModelOptionValue]);

  useEffect(() => {
    if (!embedded) return;
    if (initialVehicle || editingVehicle) {
      setShowAddVehicleModal(true);
      return;
    }
    setShowVehicleTypeModal(true);
  }, [embedded, initialVehicle, editingVehicle]);

  const handleVehicleTypeSelect = (type) => {
    setVehicleType(type);
    setShowVehicleTypeModal(false);
    // Small delay for smooth transition
    setTimeout(() => {
      setShowAddVehicleModal(true);
    }, 200);
  };

  // Keep a ref of the current maker so handleModelChange always has the latest value
  // (avoids stale closure issue since setSelectedMaker is async)
  const currentMakerRef = useRef("");

  const handleMakerChange = async (e) => {
    const maker = e.target.value;
    currentMakerRef.current = maker;
    setSelectedMaker(maker);
    setSelectedModel(""); setSelectedModelId(""); setSelectedYear(""); setSelectedModification("");
    setSelectedMonth("");
    setModels([]); setYears([]); setMonths([]); setModifications([]);
    if (maker) {
      try {
        if (fetchModels) {
          const apiModels = await fetchModels(maker);
          if (apiModels && apiModels.length > 0) { setModels(apiModels); return; }
        }
      } catch { }
      // Fallback to local data
      const type = vehicleType || 'car';
      const localModels = getModelsForMaker(maker, type);
      setModels(localModels.map(m => ({ value: m, label: m.replace(/_/g, ' ') })));
    }
  };

  const handleModelChange = useCallback(async (e) => {
    const modelValue = e.target.value;
    const selected = models.find((m) => getModelOptionValue(m) === String(modelValue));
    const model = selected?.label || selected?.value || String(modelValue || '');
    const resolvedModelId = String(selected?.modelId || '').trim();
    setSelectedModelId(modelValue);
    setSelectedModel(model);
    setSelectedYear(""); setSelectedModification("");
    setSelectedMonth("");
    setYears([]); setMonths([]); setModifications([]);
    // Use ref to get the latest maker (avoids stale state value)
    const maker = currentMakerRef.current || selectedMaker;
    if (maker && model) {
      try {
        const [yr, vr] = await Promise.all([
          fetchYears ? fetchYears(maker, model, { modelId: resolvedModelId || undefined }) : Promise.resolve([]),
          fetchVariants ? fetchVariants(maker, model, '', { modelId: resolvedModelId || undefined }) : Promise.resolve([])
        ]);
        if (yr && yr.length > 0) { setYears(yr); setModifications(vr.map(v => v.value || v)); return; }
      } catch { }
      // Fallback to local data
      const type = vehicleType || 'car';
      const localYears = getYearsForModel(maker, model, type);
      setYears(localYears.map(y => ({ value: y.toString(), label: y.toString() })));
      setModifications([]);
    }
  }, [models, getModelOptionValue, selectedMaker, vehicleType, fetchYears, fetchVariants]);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    setSelectedMonth("");
    setSelectedModification("");
  };

  // When year changes, fetch month + year-specific variants from backend trims
  useEffect(() => {
    const maker = currentMakerRef.current || selectedMaker;
    if (!maker || !selectedModel || !selectedYear) {
      setMonths([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const [apiMonths, apiVariants] = await Promise.all([
          fetchMonths ? fetchMonths(maker, selectedModel, selectedYear, { modelId: selectedModelId || undefined }) : Promise.resolve([]),
          fetchVariants ? fetchVariants(maker, selectedModel, selectedYear, { modelId: selectedModelId || undefined }) : Promise.resolve([])
        ]);
        if (cancelled) return;
        setMonths(Array.isArray(apiMonths) ? apiMonths : []);
        if (Array.isArray(apiVariants) && apiVariants.length > 0) {
          setModifications(apiVariants.map((v) => v.value || v));
          return;
        }
      } catch { }
      if (cancelled) return;
      const type = vehicleType || 'car';
      const localMods = getModificationsForYear(maker, selectedModel, selectedYear, type);
      setModifications(localMods);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedMaker, selectedModel, selectedModelId, selectedYear, vehicleType, fetchMonths, fetchVariants]);

  const handleSaveVehicle = async () => {
    if (!selectedMaker || !selectedModel || !selectedYear) {
      showToast.warning('Please select Maker, Model, and Year');
      return;
    }

    const vehicleData = {
      make: selectedMaker,
      model: selectedModel,
      modelId: (() => {
        const selected = models.find((m) => getModelOptionValue(m) === String(selectedModelId));
        const id = String(selected?.modelId || '').trim();
        return id || undefined;
      })(),
      variant: selectedModification,
      year: selectedYear.toString(),
      month: selectedMonth || undefined,
      registrationNumber: numberPlate.trim() || undefined,
      vin: vin.trim() || undefined,
      type: vehicleType || "car",
      fuelType: fuelType || undefined,
      photo: vehiclePhoto || undefined,
    };

    const vehicleToEdit = initialVehicle || editingVehicle;
    try {
      if (vehicleToEdit) {
        await updateVehicle(vehicleToEdit._id || vehicleToEdit.id, vehicleData);
        showToast.success(`Vehicle updated! ${selectedMaker} ${selectedModel} (${selectedYear})`);
        setEditingVehicle(null);
      } else {
        await addVehicle(vehicleData);
        showToast.success(`Vehicle saved! ${selectedMaker} ${selectedModel} (${selectedYear})`);
      }
    } catch (err) {
      showToast.error(err?.message || 'Could not save vehicle');
      return;
    }

    setSelectedMaker("");
    setSelectedModel("");
    setSelectedModelId("");
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedModification("");
    setNumberPlate("");
    setVin("");
    setFuelType("");
    setVehiclePhoto("");
    setModels([]);
    setYears([]);
    setMonths([]);
    setModifications([]);
    setShowAddVehicleModal(false);
    setShowVehicleTypeModal(false);
    setVehicleType("");

    if (onClose) {
      onClose();
    }
  };

  const dismissEmbeddedFlow = () => {
    setShowVehicleTypeModal(false);
    setShowAddVehicleModal(false);
    if (embedded && onClose) onClose();
  };

  const handleCloseAddVehicleModal = () => {
    setShowAddVehicleModal(false);
    setVehicleType("");
    setEditingVehicle(null);
    // Reset form when closing
    setSelectedMaker("");
    setSelectedModel("");
    setSelectedModelId("");
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedModification("");
    setNumberPlate("");
    setVin("");
    setFuelType("");
    setVehiclePhoto("");
    setModels([]);
    setYears([]);
    setMonths([]);
    setModifications([]);
  };

  const vehicleCardClass =
    'w-full min-w-0 shrink-0 snap-start sm:snap-align-none sm:shrink sm:w-full';

  return (
    <>
      {!embedded && (
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-8 pb-24 sm:pt-12 sm:pb-12 md:pt-14 md:pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title Section - Aligned with Header */}

          {/* Vehicle Cards Grid */}
          {sortedVehicles.length === 0 ? (
            <div className="mb-6 sm:mb-8 md:mb-10 w-full">
              <h2 className="text-left text-base sm:text-lg md:text-xl font-bold tracking-tight text-gray-900 mb-4 sm:mb-5 max-w-2xl">
                Add Your Car to Get Exact Parts
              </h2>
              <div className="flex justify-center">
              <button
                onClick={() => setShowVehicleTypeModal(true)}
                className="w-full max-w-md bg-gradient-to-br from-white via-blue-50 to-blue-100 rounded-xl shadow-md border border-gray-200 p-6 flex flex-col items-center gap-4"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md">
                  <FaPlus className="text-white text-lg" />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-900 text-lg">Add Vehicle</div>
                  <div className="text-sm text-gray-500">Add your car or bike to get personalized results</div>
                </div>
              </button>
              </div>
            </div>
          ) : (
            <div className="mb-3 sm:mb-4 md:mb-6 lg:mb-8 w-full">
              <div className="mb-4 sm:mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between w-full">
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight text-gray-900">
                    Your vehicles
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5 leading-snug">
                    Select a vehicle for parts · open health passport below
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <p className="text-xs font-medium text-gray-400">
                    {sortedVehicles.length} saved
                  </p>
                  <Link
                    to="/vehicle-picker"
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 transition-colors group"
                  >
                    See more
                    <FaChevronRight className="text-xs transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 mb-3 sm:hidden">
                Swipe sideways to see all vehicles →
              </p>
            <div
              className="
                flex gap-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory
                pb-3 -mx-4 px-4
                sm:mx-0 sm:px-0 sm:pb-0 sm:overflow-visible sm:snap-none
                sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 sm:gap-4 md:gap-5
                w-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
              "
            >
              {/* Vehicle Cards */}
              {sortedVehicles.map((vehicle) => {
                const isSelected = selectedVehicle && (selectedVehicle._id || selectedVehicle.id) === (vehicle._id || vehicle.id);
                return (
                  <motion.div
                    key={vehicle._id || vehicle.id}
                    className={`${vehicleCardClass} w-[min(88vw,340px)] sm:w-full bg-white rounded-2xl shadow-soft hover:shadow-medium overflow-hidden transition-all duration-300 group flex flex-col min-h-[260px] sm:min-h-[280px] h-full ${isSelected
                      ? 'border-2 border-blue-500 ring-4 ring-blue-500/10'
                      : 'border border-gray-100'
                      }`}
                    whileHover={{ y: -3 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => selectVehicle(vehicle)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          selectVehicle(vehicle);
                        }
                      }}
                      className="flex flex-col flex-1 text-left w-full cursor-pointer"
                    >
                    {/* Vehicle Image */}
                    <div className={`relative h-28 sm:h-32 bg-gradient-to-br from-gray-50/50 to-white flex items-center justify-center overflow-hidden ${vehicle.photo ? '' : 'p-2'}`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <img loading="lazy"
                        src={
                          vehicle.photo ||
                          getVehicleImage(
                            vehicle.make,
                            stripDuplicateMakeFromModel(vehicle.make, vehicle.model)
                          )
                        }
                        alt={`${vehicle.make} ${vehicle.model}`}
                        className={`relative z-10 transform group-hover:scale-105 transition-transform duration-300 ${vehicle.photo ? 'w-full h-full object-cover' : 'max-w-full max-h-full object-contain'}`}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = VEHICLE_CARD_IMAGE_PLACEHOLDER;
                        }}
                      />
                      {/* Edit and Delete Icons */}
                      <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex gap-1 z-20 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity duration-300">
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(vehicle);
                          }}
                          className="bg-blue-500 hover:bg-blue-600 text-white p-1 sm:p-1.5 rounded-full transition-all shadow-md"
                          title="Edit"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <FaEdit className="text-[8px] sm:text-[10px] md:text-xs" />
                        </motion.button>
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(vehicle);
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white p-1 sm:p-1.5 rounded-full transition-all shadow-md"
                          title="Delete"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <FaTrash className="text-[8px] sm:text-[10px] md:text-xs" />
                        </motion.button>
                      </div>
                      {/* Vehicle Type Badge */}
                      <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 z-20">
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[7px] sm:text-[8px] md:text-[9px] font-bold ${vehicle.type === 'twoWheeler'
                          ? 'bg-orange-500 text-white'
                          : 'bg-blue-500 text-white'
                          }`}>
                          {vehicle.type === 'twoWheeler' ? 'BIKE' : 'CAR'}
                        </span>
                      </div>
                      {/* Selected Tick Badge */}
                      {isSelected && (
                        <div className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 z-20">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                          >
                            <FaCheckCircle className="text-green-500 text-base sm:text-lg md:text-xl drop-shadow" />
                          </motion.div>
                        </div>
                      )}
                    </div>

                    {/* Vehicle Details */}
                    <div className="p-3 bg-white border-t border-gray-100 flex flex-col flex-1">
                      <h3 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2">
                        {getVehicleCardTitle(vehicle)}
                      </h3>
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        {(vehicle.variant || getEngineInfo(vehicle.variant)) && (
                          <p className="font-medium text-blue-600 truncate">
                            {vehicle.variant || getEngineInfo(vehicle.variant)}
                          </p>
                        )}
                        <p>
                          <span className="text-gray-500">Year</span>{' '}
                          <span className="font-semibold text-gray-800">{vehicle.year}</span>
                        </p>
                        {vehicle.registrationNumber && (
                          <p className="truncate font-mono text-[11px] text-gray-500">
                            {vehicle.registrationNumber}
                          </p>
                        )}
                      </div>
                    </div>
                    </div>
                    <div className="px-3 pb-3 pt-0">
                      <button
                        type="button"
                        onClick={() => handleViewPassport(vehicle)}
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 transition-colors"
                      >
                        <FaHeartbeat className="text-[10px]" />
                        Health Passport
                      </button>
                    </div>
                  </motion.div>
                );
              })}

              {/* Add New Vehicle Card */}
              <button
                type="button"
                onClick={() => setShowVehicleTypeModal(true)}
                className={`${vehicleCardClass} w-[min(88vw,340px)] sm:w-full min-h-[260px] sm:min-h-[280px] h-full rounded-2xl border-2 border-dashed border-gray-200 bg-white/60 hover:border-blue-300 hover:bg-blue-50/30 transition-all flex flex-col items-center justify-center p-5 sm:p-6 text-center`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md mb-3">
                  <FaPlus className="text-white text-lg" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Add vehicle</h3>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed max-w-[200px]">
                  Add your car or bike for personalized parts and service history
                </p>
              </button>
            </div>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Vehicle Type Selection Modal - Compact & Attractive Design */}
      <AnimatePresence>
        {showVehicleTypeModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
              onClick={() => {
                if (embedded) dismissEmbeddedFlow();
                else setShowVehicleTypeModal(false);
              }}
            >
              <motion.div
                initial={{ scale: 0.96, opacity: 0, y: 10 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.96, opacity: 0, y: 10 }}
                transition={{
                  type: "spring",
                  damping: 30,
                  stiffness: 400,
                  duration: 0.25
                }}
                className="bg-slate-900 rounded-xl shadow-2xl p-6 sm:p-8 max-w-md w-full border border-slate-600 relative overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Subtle Background  */}
                <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, rgba(148,163,184,0.15) 1px, transparent 0)`,
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>

                {/* Close Button */}
                <button
                  onClick={dismissEmbeddedFlow}
                  className="absolute top-3 right-3 text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition-all duration-200 z-10"
                  aria-label="Close modal"
                >
                  <FaTimes className="text-lg" />
                </button>

                {/* Compact Header */}
                <div className="relative z-10 mb-5">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-100 mb-1 tracking-tight">
                    Select Vehicle Type
                  </h3>
                  <p className="text-sm text-slate-400">Choose whether you want to add a car or a bike</p>
                </div>

                {/* Compact Vehicle Type Cards */}
                <div className="grid grid-cols-2 gap-4 relative z-10">
                  {/* Car Option */}
                  <button
                    type="button"
                    onClick={() => handleVehicleTypeSelect("car")}
                    className="flex items-center gap-4 px-4 py-5 rounded-lg bg-slate-800/90 border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200 text-left"
                  >
                    <div className="w-12 h-12 rounded-md bg-gradient-to-br from-blue-500 to-blue-400 flex items-center justify-center shadow-md shrink-0">
                      <FaCar className="text-white text-[18px]" />
                    </div>
                    <div className="text-left min-w-0">
                      <div className="font-semibold text-slate-100 text-base">Car</div>
                    </div>
                  </button>

                  {/* Two Wheeler Option */}
                  <button
                    type="button"
                    onClick={() => handleVehicleTypeSelect("twoWheeler")}
                    className="flex items-center gap-4 px-4 py-5 rounded-lg bg-slate-800/90 border border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200 text-left"
                  >
                    <div className="w-12 h-12 rounded-md bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center shadow-md shrink-0">
                      <FaMotorcycle className="text-white text-[18px]" />
                    </div>
                    <div className="text-left min-w-0">
                      <div className="font-semibold text-slate-100 text-base">Two Wheeler</div>
                    </div>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add Vehicle Form Modal - Horizontal Layout */}
      <AnimatePresence>
        {showAddVehicleModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
              onClick={handleCloseAddVehicleModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 50 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 50 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-gradient-to-br from-white via-blue-50 to-blue-100 rounded-2xl shadow-2xl p-6 md:p-8 max-w-5xl w-full relative max-h-[90vh] overflow-y-auto font-sans text-slate-800"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={handleCloseAddVehicleModal}
                  className="absolute top-4 right-4 text-blue-700 hover:text-blue-900 p-2 z-10"
                >
                  <FaTimes className="text-xl" />
                </button>

                {/* Title */}
                <h3 className="text-xl md:text-2xl font-extrabold text-blue-900 mb-6 text-center">
                  {editingVehicle ? "Edit" : "Add"} {vehicleType === "car" ? "Car" : vehicleType === "twoWheeler" ? "Two Wheeler" : "Vehicle"}
                </h3>

                {/* Number Plate Input */}
                <div className="mb-6">
                  <h4 className="text-lg mb-2 text-blue-900">Find your {vehicleType === "car" ? "car" : "vehicle"} by Number Plate:</h4>
                  <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2 shadow-sm bg-white">
                    <span className="bg-gray-100 px-3 py-2 rounded-l text-sm text-slate-700">IND</span>
                    <input
                      type="text"
                      placeholder="DL1AA2345"
                      value={numberPlate}
                      onChange={(e) => setNumberPlate(e.target.value)}
                      className="flex-1 px-3 py-2 outline-none text-slate-800 bg-transparent"
                    />
                    <FaSearch className="text-blue-600 text-xl cursor-pointer" />
                  </div>
                </div>

                <p className="text-gray-500 my-4 text-center">OR</p>

                {/* Fuel Type Selection */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Select Fuel Type:</h4>
                  <div className="flex flex-wrap gap-2">
                    {(vehicleType === "car"
                      ? ["Petrol", "Diesel", "CNG", "EV"]
                      : ["Petrol", "CNG", "EV"]
                    ).map((fuel) => (
                      <button
                        key={fuel}
                        type="button"
                        onClick={() => setFuelType(fuel)}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${fuelType === fuel
                          ? "bg-blue-600 text-white border-blue-600 shadow-md"
                          : "bg-white text-slate-700 border-gray-200 hover:border-blue-400 hover:bg-blue-50"
                          }`}
                      >
                        {fuel}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dropdowns Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6">
                  {/* Car Maker */}
                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1">Maker</label>
                    <select
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm md:text-base appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      value={selectedMaker}
                      onChange={handleMakerChange}
                    >
                      <option value="">Select Maker</option>
                      {makes.map((maker) => (
                        <option key={maker.value || maker} value={maker.value || maker}>
                          {maker.label || maker.value || maker}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Model */}
                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1">Model</label>
                    <select
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm md:text-base appearance-none disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      value={selectedModelId}
                      onChange={handleModelChange}
                      disabled={!selectedMaker}
                    >
                      <option value="">Select Model</option>
                      {models.map((model) => (
                        <option key={getModelOptionValue(model) || model.label || model.value || model} value={getModelOptionValue(model)}>
                          {model.label || model.value || model}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Year */}
                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1">Year</label>
                    <select
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm md:text-base appearance-none disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      value={selectedYear}
                      onChange={handleYearChange}
                      disabled={!selectedModel}
                    >
                      <option value="">Select Year</option>
                      {years.map((year) => (
                        <option key={year.value || year} value={year.value || year}>
                          {year.label || year.value || year}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Month */}
                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1">Month</label>
                    <select
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm md:text-base appearance-none disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      disabled={!selectedYear}
                    >
                      <option value="">Select Month</option>
                      {monthOptionsForSelect.map((month) => (
                        <option key={month.value || month.label} value={month.value || month}>
                          {month.label || month.value || month}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1">Variant</label>
                    <select
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm md:text-base appearance-none disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      value={selectedModification}
                      onChange={(e) => setSelectedModification(e.target.value)}
                      disabled={!selectedModel}
                    >
                      <option value="">Select Variant</option>
                      {modifications.map((mod) => (
                        <option key={mod} value={mod}>
                          {mod}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* VIN Input */}
                <div className="mt-6">
                  <input
                    type="text"
                    placeholder="Enter VIN (Optional)"
                    value={vin}
                    onChange={(e) => setVin(e.target.value)}
                    className="w-full border px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  />
                </div>

                {/* Vehicle Photo Upload */}
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-blue-900 mb-2">Vehicle Photo (Optional)</label>
                  <div
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full h-32 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 bg-gray-50 flex items-center justify-center cursor-pointer transition-all overflow-hidden relative"
                  >
                    {vehiclePhoto ? (
                      <>
                        <img src={vehiclePhoto} alt="Vehicle" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setVehiclePhoto(""); }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full text-xs z-10"
                        >
                          <FaTimes className="text-[10px]" />
                        </button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <FaCamera className="text-gray-400 text-xl" />
                        <span className="text-xs text-gray-400">Click to upload vehicle photo</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        showToast.warning('Photo must be under 2MB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => setVehiclePhoto(reader.result);
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                  />
                </div>

                {/* Add Vehicle Button */}
                <motion.button
                  onClick={handleSaveVehicle}
                  className="w-full px-6 py-3 md:py-4 mt-4 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold hover:from-blue-700 hover:to-blue-600 transition-colors text-sm md:text-base"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Add Vehicle
                </motion.button>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Vehicle Confirmation Modal */}
      <AnimatePresence>
        {vehicleToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={cancelDelete}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-8 w-[90%] md:w-[500px] shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={cancelDelete}
                className="absolute top-4 right-4 text-gray-800 hover:text-red-600 text-xl font-bold transition-colors"
              >
                <FaTimes className="text-2xl" />
              </button>

              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                <span className="text-blue-800">Delete</span>{' '}
                <span className="text-blue-400">Vehicle</span>
              </h2>

              <p className="text-blue-800 font-bold text-lg mb-4">
                {vehicleToDelete.make} {vehicleToDelete.model} {getEngineInfo(vehicleToDelete.variant)} {vehicleToDelete.variant || ''} {vehicleToDelete.year}
              </p>

              <p className="text-gray-700 text-base mb-8">
                Are you sure that you want to delete this vehicle?
              </p>

              <button
                onClick={confirmDelete}
                className="w-full bg-blue-400 hover:bg-blue-500 text-white font-semibold py-4 px-6 rounded-lg text-lg transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]"
              >
                Delete
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SearchSection;
